import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { Label, Message, Spinner, Tooltip } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { SearchableDropdown } from 'components/SearchableDropdown/SearchableDropdown';
import { CriteriaItem } from 'components/Signup/NewSignupForm/CriteriaItem/CriteriaItem';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { TCompanySearchResultDE } from 'lendingApi/lendingApi.types';
import { useGetCompanySearch } from 'lendingApi/lendingApiHooks';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { getLegalForm, useCompanyInput } from './CompanyInputDE.helpers';
import styles from './CompanyInputDE.module.css';
import { ManualCompanyInputDE } from './ManualCompanyInputDE/ManualCompanyInputDE';

type TProps = {
  searchedCompaniesCount: number;
  setSearchedCompaniesCount: Dispatch<SetStateAction<number>>;
};

export const CompanyInputDE = ({
  searchedCompaniesCount,
  setSearchedCompaniesCount,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const {
    isManualInput,
    toggleManualInput,
    denormalisedSelectedCompany,
    resetCompanyDetails,
    updateCompanyDetailsFromLookup,
    lookupError,
  } = useCompanyInput();

  const [hasLegalForm, setHasLegalForm] = useState(
    Boolean(denormalisedSelectedCompany?.legalForm),
  );
  const [isFetchingLegalForm, setIsFetchingLegalForm] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState<
    TCompanySearchResultDE | undefined
  >(denormalisedSelectedCompany);

  const [searchQuery, setSearchQuery] = useState(
    denormalisedSelectedCompany?.name || '',
  );
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [companyOptions, setCompanyOptions] = useState<
    TCompanySearchResultDE[]
  >([]);

  const { checkIntroducerPermission } = useIntroducerData();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  const {
    data: companySearchData,
    isFetching,
    isError,
  } = useGetCompanySearch({
    searchQuery: debouncedQuery,
    enabled: !!debouncedQuery && searchQuery.length >= 3,
  });

  useEffect(() => {
    const setQueryTimer = setTimeout(() => {
      if (searchQuery && searchQuery.length >= 3) {
        setSearchedCompaniesCount((prevCount) => prevCount + 1);
        setDebouncedQuery(searchQuery);
      } else {
        setDebouncedQuery('');
      }
    }, 300);

    return () => clearTimeout(setQueryTimer);
  }, [searchQuery, setSearchedCompaniesCount]);

  useEffect(() => {
    if (debouncedQuery.length < 3) {
      setCompanyOptions([]);
      setSelectedCompany(undefined);
      return;
    }

    if (companySearchData) {
      setCompanyOptions(companySearchData.data);
    }

    if (isError) {
      setCompanyOptions([]);
    }
  }, [companySearchData, isError, debouncedQuery]);

  useEffect(() => {
    if (!isManualInput) {
      setSelectedCompany(denormalisedSelectedCompany);
      setHasLegalForm(Boolean(denormalisedSelectedCompany?.legalForm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualInput, denormalisedSelectedCompany?.name]);

  const handleChange = async (value: TCompanySearchResultDE) => {
    setIsFetchingLegalForm(true);
    const legalForm = await getLegalForm(value.crefo_id);
    setSelectedCompany(value);
    setHasLegalForm(Boolean(legalForm));
    updateCompanyDetailsFromLookup(value, legalForm);
    trackEvent(AMPLITUDE_EVENTS.companySearchSelected, {
      'Company name': value.name,
    });
    setIsFetchingLegalForm(false);
  };

  const handleClear = () => {
    setSelectedCompany(undefined);
    setHasLegalForm(false);
    resetCompanyDetails();
  };

  const handleToggle = () => {
    toggleManualInput();
    trackEvent(AMPLITUDE_EVENTS.companyInputToggleClicked, {
      'CTA text': isManualInput ? 'Search your company' : 'Enter manually',
      searched_companies_count: searchedCompaniesCount,
    });
  };

  const renderLabel = () => (
    <div className={styles.label}>
      {isManualInput ? (
        <div className={styles.labelWithTooltip}>
          <Label htmlFor="company.name">
            <Trans>Business name</Trans>
          </Label>
          <Tooltip
            dataTestId="manual-input-tooltip"
            content={t`Enter your client’s company details here.`}
          />
        </div>
      ) : (
        <Combobox.Label>
          <Label>
            <Trans>Business name</Trans>
          </Label>
        </Combobox.Label>
      )}

      <ButtonLink
        data-testid="input-mode-toggle"
        onClick={handleToggle}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        type="button"
      >
        {isManualInput
          ? t`Go back to search your company`
          : t`Company not found?`}
      </ButtonLink>
    </div>
  );

  if (isManualInput) {
    return <ManualCompanyInputDE labelComponent={renderLabel()} />;
  }

  return (
    <div data-testid="crefo-search">
      <SearchableDropdown
        labelComponent={renderLabel()}
        searchQuery={selectedCompany?.name}
        onChange={handleChange}
        onSearch={setSearchQuery}
        options={companyOptions}
        optionsLoading={isFetching}
        optionRenderer={(company) => `${company.name} (${company.city})`}
        valueRenderer={(company) => company.name}
        placeholder={t`Search...`}
        onClear={handleClear}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      />
      {hasLegalForm && (
        <CriteriaItem hasPassed className={styles.confirmation}>
          <Trans>We were able to copy the legal form from the Crefo.</Trans>
        </CriteriaItem>
      )}
      {isFetchingLegalForm && <Spinner className={styles.spinner} />}

      {lookupError && (
        <Message
          className={styles.lookupError}
          message={lookupError}
          type="error"
        />
      )}
      {selectedCompany && !hasLegalForm && (
        <CriteriaItem className={styles.confirmation}>
          <p className={styles.enterManuallyText}>
            <Trans>
              It is not possible to retrieve company information via Crefo,
              please enter{' '}
              <ButtonLink
                className={styles.enterManually}
                type="button"
                onClick={handleToggle}
                disabled={isDisabled}
                aria-disabled={isDisabled}
              >
                here
              </ButtonLink>{' '}
              the data manually.
            </Trans>
          </p>
        </CriteriaItem>
      )}
    </div>
  );
};
